import { inject, Injectable } from '@angular/core';
import { Geolocation, Position } from '@capacitor/geolocation';
import { AlertService } from './alert.service';
import * as Sentry from '@sentry/capacitor';

@Injectable({
    providedIn: 'root'
})
export class GeolocationService
{
    private alertService = inject(AlertService);

    async getCurrentPosition(): Promise<Position | null>
    {
        try
        {
            const granted = await this.requestPermission();

            if (!granted)
            {
                return null;
            }

            Sentry.addBreadcrumb({
                category: 'geolocation',
                message: 'Requesting current position',
                level: 'info',
            });

            const position = await Geolocation.getCurrentPosition();
            console.log(position);

            return position;
        }
        catch (error: any)
        {
            console.error('getCurrentPosition', error);

            if (error && 'message' in error)
            {
                if (error.message.toLowerCase().includes('user denied geo'))
                {
                    await this.alertService.show('The app was not able to get your location, please grant location and coarse location permissions in your device settings.');
                }
            }

            return null;
        }
    }

    async getGeolocationPermissionsMessage(): Promise<string>
    {
        try
        {
            const permissions = await Geolocation.checkPermissions();
            console.info('await Geolocation.checkPermissions()', permissions);

            let message = `<div>Geolocation Permissions:</div><ul class="m-0">`;
            message += `<li>location: <b>${permissions.location}</b></li>`;
            message += `<li>coarse location: <b>${permissions.coarseLocation}</b></li>`;
            message += `</ul>`;

            return message;
        }
        catch (error: any)
        {
            console.error('getGeolocationPermissionsMessage()', error);
            Sentry.captureException(error);

            return `<div>Geolocation Permissions: <b>not able to check due to an error</b></div>`;
        }
    }

    private async requestPermission(): Promise<boolean>
    {
        try
        {
            const permissionStatus = await Geolocation.requestPermissions();
            console.info('Geolocation Geolocation.requestPermissions()', permissionStatus);
            let granted = true;

            if (permissionStatus.location !== 'granted')
            {
                granted = false;
            }

            if (permissionStatus.coarseLocation !== 'granted')
            {
                granted = false;
            }

            if (!granted)
            {
                await this.alertService.show('To use the geolocation feature, please grant location and coarse location permissions in your device settings.');
            }

            return granted;
        }
        catch (error: any)
        {
            console.error('requestPermission', error);
            Sentry.captureException(error);

            if (error && 'message' in error)
            {
                if (error.message.toLowerCase().includes('not implemented on web'))
                {
                    return true;
                }
            }

            await this.alertService.show('To use the geolocation feature, please grant location and coarse location permissions in your device settings.');

            return false;
        }
    }
}
